<template>
  <el-row v-loading="isReading" class="jr-file-input">
    <el-col :span="12">
      <input
        :id="fileInputId"
        @change="changeFile"
        ref="inputFileRef"
        :accept="allowExts.join(',')"
        type="file"
        class="file-input"
        style="display: none"
      />
      <el-input
        v-if="displayFileName == null && localFile == null"
        class="input-with-select"
        @click="openSelectDialog"
        :disabled="disabled"
        :readonly="true"
        ref="dummyInputRef"
        :size="size"
      />
      <jr-button
        v-else
        class="file-open-btn w-100 px text-omit align-left file-name-max-width"
        type="text"
        :size="size"
        @click="downloadFile"
      >
        <el-tooltip effect="dark" :content="displayFileName" placement="top-start">
          <span class="file-name">{{ displayFileName }}</span>
        </el-tooltip>
      </jr-button>
    </el-col>
    <el-col :span="6" class="align-right px">
      <jr-button
        class="file-select-btn"
        type="primary"
        icon="el-icon-files"
        @click="openSelectDialog"
        :disabled="disabled"
        :size="size"
      >
        参照...
      </jr-button>
    </el-col>
    <el-col :span="6" class="align-left px">
      <jr-button
        class="file-delete-btn"
        type="info"
        icon="el-icon-delete"
        @click="deleteFile"
        :disabled="disabled || ((modelValue === -1 || modelValue == null) && localFile == null)"
        :size="size"
      >
        削除
      </jr-button>
    </el-col>
  </el-row>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, watch } from 'vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import Message from '@/services/message'
import { addMessageQueue, MESSAGE_TYPE_CONFIRM, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import { download, getExt, isAllowExt, getMimeTypeFromBase64, byteToMb, isOverFileNameMaxLength } from '@/services/file'
import { CreateRequest } from '@/requests/fileUpload'
import { CreateRequest as FileDownloadRequest } from '@/requests/fileDownload'
import { FileNameReadRequest } from '@/requests/file'
import { FileDownloadResponse, FileResponse, FileUpload } from '@/api'
import { AxiosResponse } from 'axios'
import { ElInput } from 'element-plus'
import { FileTypes } from '@/constants/enums'
import { MAX_UPLOAD_SIZE, FILE_NAME_MAX_LENGTH } from '@/constants/backendConstants'
import { v4 as uuidv4 } from 'uuid'

/**
 * id用にユニークな文字列を作る
 */
const getUniqueStr = () => {
  return uuidv4()
}

export default defineComponent({
  name: 'JrFileInput',
  emits: ['update:modelValue', 'update:selected', 'change', 'selectedFileId'],
  components: {
    JrButton,
  },
  props: {
    // ファイルID
    modelValue: {
      type: Number,
      required: false,
      default: null,
    },
    // ファイル種別
    fileType: {
      type: Number,
      required: true,
    },
    // 許可拡張子配列(.pdfなど)
    allowExts: {
      type: Array as PropType<string[]>,
      required: true,
    },
    // 最大サイズ byte単位
    maxSize: {
      type: Number,
      required: false,
      default: MAX_UPLOAD_SIZE,
    },
    fileNameMaxLength: {
      type: Number,
      required: false,
      default: FILE_NAME_MAX_LENGTH,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 確認ダイアログ設定
    confirmDialog: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    // ファイル読み込み中フラグ
    const isReading = ref<boolean>(false)
    // input要素のID
    const fileInputId = getUniqueStr()
    // input要素のref
    const inputFileRef = ref<HTMLInputElement>()
    const dummyInputRef = ref<typeof ElInput>()
    // サーバーファイル情報
    const serverFileName = ref<string | null>(null)

    // ローカルファイル
    const localFile = ref<File | null>(null)
    // ファイルデータ
    const base64 = ref<string | null>(null)
    // 表示ファイル名
    const displayFileName = computed(() =>
      // ローカルファイルがあればそのファイル名、無ければ親から指定されたファイル名を表示する
      localFile.value != null ? localFile.value.name : serverFileName.value
    )
    // 要素のref
    const selectedFileId = ref<number | null>()

    // ファイル情報の取得
    const getServerFileName = async (fileId: number) => {
      const request = new FileNameReadRequest(fileId)
      await request.exec()
      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<FileResponse>
        serverFileName.value = data.file_name
        context.emit('update:selected', true)
      }
    }

    // ファイルID変更時にサーバーからファイル名を取得する
    watch(
      () => props.modelValue,
      async (fileId: number | null) => {
        if (localFile.value == null && fileId !== -1 && fileId !== null) {
          // ローカルファイルが無い、かつファイルIDの指定がある際に取得する
          getServerFileName(fileId)
        }
      },
      { immediate: true }
    )

    /**
     * ファイル変更時の処理
     */
    const changeFile = () => {
      if (inputFileRef.value == null) {
        // DOMが無い場合は何もしない
        return
      }
      if (inputFileRef.value.files == null || inputFileRef.value.files?.length == 0) {
        // ファイル要素では無い場合(通常はあり得ない)かファイルが選択されていないときは何もしない
        return
      }
      const file = inputFileRef.value.files[0]

      if (!isAllowExt(file.name, props.allowExts)) {
        // 許可されていない拡張子の場合、エラーとし選択ファイルをリセットする
        inputFileRef.value.value = ''
        addMessageQueue(
          MESSAGE_TYPE_ERROR,
          Message.bind(Message.codes.FILE_EXT_NOT_ALLOW, { ext_list: props.allowExts.join(',') })
        )
        return
      }
      if (props.maxSize < file.size) {
        // ファイルサイズが規定より大きい場合エラーとし選択ファイルをリセットする
        inputFileRef.value.value = ''
        addMessageQueue(
          MESSAGE_TYPE_ERROR,
          Message.bind(Message.codes.FILE_MAX_SIZE_OVER, { max: byteToMb(props.maxSize) + 'MB' })
        )
        return
      }
      if (isOverFileNameMaxLength(file.name, props.fileNameMaxLength)) {
        // 拡張子含むファイル名が規定より大きい場合エラーとし選択ファイルをリセットする
        inputFileRef.value.value = ''
        addMessageQueue(
          MESSAGE_TYPE_ERROR,
          Message.bind(Message.codes.FILE_NAME_MAX_LENGTH_OVER, { max: String(props.fileNameMaxLength) + '文字' })
        )
        return
      }

      // ファイルの読み込み
      const reader = new FileReader()

      // 読み込み完了時のリスナー
      reader.onload = () => {
        isReading.value = false
        if (reader.result == null) {
          return
        }
        // mimetypeが取得できるか試行する
        try {
          getMimeTypeFromBase64(reader.result as string)
        } catch {
          // 読み込み失敗時はエラー
          if (inputFileRef.value != null) {
            inputFileRef.value.value = ''
          }
          addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.FILE_READ_ERROR)
          return
        }

        // 親に選択状態を通知
        context.emit('update:modelValue', props.nullable ? null : -1)
        context.emit('update:selected', true)
        if (dummyInputRef.value != null) {
          dummyInputRef.value.blur()
        }
        // 選択したファイルIDを保持
        selectedFileId.value = props.modelValue

        if (props.confirmDialog) {
          // 確認ダイアログ表示
          addMessageQueue(
            MESSAGE_TYPE_CONFIRM,
            Message.codes.FILE_UPLOAD,
            async () => {
              // base64encodeされた内容を保持する
              base64.value = reader.result as string
              localFile.value = file
              // ファイルをアップロード
              await uploadFile()
            },
            async () => {
              // いいえを押下
              if (selectedFileId.value == null) {
                deleteFileAction()
              }
            }
          )
        } else {
          // base64encodeされた内容を保持する
          base64.value = reader.result as string
          localFile.value = file
        }
      }
      // エラー時
      reader.onerror = () => {
        isReading.value = false
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.FILE_READ_ERROR)
      }
      isReading.value = true
      reader.readAsDataURL(file)
      // refでDOM要素を引っ張ってvalueを消す(2回連続同じファイルをあげた時のバグ解消のため)
      inputFileRef.value.value = ''
    }

    /**
     * ファイルをダウンロードする
     */
    const downloadFile = async () => {
      if (base64.value == null) {
        const request = new FileDownloadRequest({
          file_id: props.modelValue,
        })
        await request.exec()
        if (request.success) {
          const { data } = request.getResponse() as AxiosResponse<FileDownloadResponse>
          // ダウンロード用情報
          base64.value = 'data:' + data.mime_type + ';base64,' + data.file
        } else {
          // ここを通ることは基本的にないが型エラー対応の為記載
          base64.value = ''
        }
      }
      const fileName = displayFileName.value
      if (fileName == null) {
        // ファイル名が取得できない場合は存在しない(そもそもダウンロードできない)ため、ここに入るパターンは例外となる
        throw new Error('file not found')
      }

      download(base64.value, fileName, getMimeTypeFromBase64(base64.value))
    }

    /**
     * ファイルをアップロードする
     */
    const uploadFile = async (errorCallback?: () => void, isGenerateTypeInMsg = false) => {
      if (localFile.value == null || base64.value == null) {
        // ファイルが無い場合は送信しない
        return true
      }
      if (props.modelValue != null && props.modelValue >= 0 && !props.confirmDialog) {
        // 確認ダイアログを使わない（DBを親コンポーネントで更新しない）
        // かつ既にファイルIDが有る場合(アップロード済み)の場合は送信しない
        return true
      }

      const ext = getExt(localFile.value.name)
      if (ext == null) {
        // 通常、アップロードする際はファイルが選択されており、選択時に拡張子のバリデーションが行われている
        // そのため、extが取得できないパターンは例外となる
        throw new Error('file ext not found')
      }
      let uploadFile: FileUpload = {
        file: base64.value,
        file_ext: ext,
        file_type: props.fileType,
        file_name: localFile.value.name,
      }
      if (props.fileType === FileTypes.ATTACHMENT || props.fileType === FileTypes.PROXIMITY_OTHER) {
        uploadFile.file_name = localFile.value.name
      }
      const request = new CreateRequest(uploadFile)

      if (errorCallback != null) {
        // エラー時のコールバックがあれば設定する
        request.setErrorCallback(errorCallback)
      }
      await request.exec()
      if (!request.success) {
        // 失敗時はfalse
        return false
      }
      const { data } = request.getResponse()
      // 親に取得したファイルIDを通知する
      context.emit('update:modelValue', data.file_id)

      if (isGenerateTypeInMsg) {
        // 汎用添付ファイルをメッセージ同時送信する際は、
        // 親のフォームのモデル情報のみ通知、以下の処理はスキップ
        return
      }

      // 親に取得したファイルIDを通知する
      context.emit('change', data.file_id)
      // 削除したファイルIDを通知
      context.emit('selectedFileId', selectedFileId.value)
      return true
    }

    /**
     * ファイルを削除する
     */
    const deleteFile = () => {
      if (inputFileRef.value == null) {
        return
      }
      // 選択したファイルIDを保持
      selectedFileId.value = props.modelValue

      if (props.confirmDialog) {
        // 確認ダイアログ表示
        addMessageQueue(MESSAGE_TYPE_CONFIRM, Message.codes.FILE_DELETE, async () => {
          deleteFileAction()
        })
      } else {
        deleteFileAction()
        if (inputFileRef.value.files && inputFileRef.value.files?.length > 0) {
          // ファイル選択済みならリセット
          inputFileRef.value.value = ''
        }
      }
    }

    /**
     * ファイルの削除処理
     */
    const deleteFileAction = () => {
      localFile.value = null
      base64.value = null
      serverFileName.value = null
      context.emit('update:modelValue', props.nullable ? null : -1)
      context.emit('update:selected', false)
      context.emit('change', null)
      if (selectedFileId.value) {
        // 削除したファイルIDを通知
        context.emit('selectedFileId', selectedFileId.value)
      }
    }

    /**
     * ファイル選択ダイアログを開く
     */
    const openSelectDialog = () => {
      const fileInputElement = document.getElementById(fileInputId)
      if (fileInputElement == null) {
        // ファイルinputのDOMが未精製の場合何もしない
        return
      }
      fileInputElement.click()
      if (dummyInputRef.value) {
        // 変更後もバリデーションが効くため一度フォーカスを当てる
        dummyInputRef.value.focus()
      }
    }
    return {
      isReading,
      displayFileName,
      changeFile,
      downloadFile,
      uploadFile,
      deleteFile,
      openSelectDialog,
      inputFileRef,
      fileInputId,
      localFile,
      dummyInputRef,
    }
  },
})
</script>
<style lang="scss" scoped>
.file-name-max-width {
  max-width: 350px;
}
</style>
